import {
	NodeSpec,
	Node as ProsemirrorNode,
	DOMOutputSpec
} from "prosemirror-model";
import { createStyleAttributeHandlers, StyleAttributes } from "./attributes/styleAttributes";

const stylesToCopy = {
	"margin": "margin",
	"marginLeft": "margin-left",
	"marginRight": "margin-right",
	"marginTop": "margin-top",
	"marginBottom": "margin-bottom",
	"padding": "padding",
	"paddingLeft": "padding-left",
	"paddingRight": "padding-right",
	"paddingTop": "padding-top",
	"paddingBottom": "padding-bottom",
	"float": "float",
	"clear": "clear",
	"width": "width",
	"height": "height",
	"background": "background",
	"backgroundColor": "background-color",
	"backgroundImage": "background-image",
	"border": "border",
	"display": "display"
} as const;

type DivLikeStyleKeysToCopy = keyof typeof stylesToCopy;

const {
	getStyleAttributes,
	setStyleAttributes,
	styleAttributeDefinitions
} = createStyleAttributeHandlers(
	stylesToCopy
);

export type DivLikeElementTag =
	| "div"
	| "aside"
	| "article"
	| "header"
	| "footer"
	| "main";

export type DivLikeNodeAttributes = {
	tag: DivLikeElementTag;
} & StyleAttributes<DivLikeStyleKeysToCopy>;

const getAttrs = (elementTag: DivLikeElementTag) =>
	(node: Node | string): DivLikeNodeAttributes => {
		if (!(node instanceof HTMLElement)) {
			throw new Error("Expected HTMLElement at this point");
		}

		const styleAttributes: StyleAttributes<DivLikeStyleKeysToCopy> = getStyleAttributes(node);

		return {
			...styleAttributes,
			tag: elementTag
		};
	};

export const divLike: NodeSpec = {
	attrs: {
		...styleAttributeDefinitions,
		tag: { default: "div" }
	},
	content: "block*",
	group: "block",
	defining: false,
	parseDOM: [
		{ tag: "div", getAttrs: getAttrs("div") },
		{ tag: "aside", getAttrs: getAttrs("aside") },
		{ tag: "article", getAttrs: getAttrs("article") },
		{ tag: "header", getAttrs: getAttrs("header") },
		{ tag: "footer", getAttrs: getAttrs("footer") },
		{ tag: "main", getAttrs: getAttrs("main") },
	],
	toDOM(node: ProsemirrorNode): DOMOutputSpec {
		const attrs = node.attrs as DivLikeNodeAttributes;

		const styleValue: string | undefined = setStyleAttributes(attrs);

		return [
			attrs.tag,
			{
				style: styleValue
			},
			0
		];
	}
};
